import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { 
  Crown, 
  TrendingUp, 
  Newspaper, 
  Zap, 
  BarChart3, 
  MessageSquare, 
  Search,
  X,
  ArrowRight
} from "lucide-react";

const LOGO_URL = "/doslions-logo.png";

export default function ProEcopreneurPopup({ isOpen, onClose, featureName }) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const benefits = [
    { icon: <MessageSquare className="w-4 h-4" />, text: "LIONAI Chat (3/day)", highlight: true },
    { icon: <TrendingUp className="w-4 h-4" />, text: "20+ Trending Stocks" },
    { icon: <Newspaper className="w-4 h-4" />, text: "Market News Feed" },
    { icon: <Zap className="w-4 h-4" />, text: "Weekly AI Picks" },
    { icon: <BarChart3 className="w-4 h-4" />, text: "Extended Data" },
    { icon: <Search className="w-4 h-4" />, text: "Search Any Asset" }
  ];

  const handleUpgrade = () => {
    onClose();
    navigate("/subscription");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Popup */}
      <div className="relative bg-[#111111] border-2 border-amber-500 rounded-lg shadow-[0_0_60px_rgba(245,158,11,0.2)] max-w-md w-full overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white z-10"
          data-testid="close-popup-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/20 to-purple-500/20 px-6 py-5 text-center border-b border-amber-500/30">
          <div className="w-16 h-16 bg-amber-500/20 mx-auto flex items-center justify-center mb-3">
            <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
            Why <span className="text-amber-500">Pro Ecopreneur</span>?
          </h2>
          {featureName && (
            <p className="text-sm text-zinc-400 mt-2">
              Unlock <span className="text-amber-500 font-semibold">{featureName}</span> and more!
            </p>
          )}
        </div>

        {/* Benefits */}
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className={`flex items-center gap-2 p-2 rounded ${
                  benefit.highlight 
                    ? 'bg-amber-500/10 border border-amber-500/30' 
                    : 'bg-zinc-900/50'
                }`}
              >
                <span className={benefit.highlight ? 'text-amber-500' : 'text-zinc-400'}>
                  {benefit.icon}
                </span>
                <span className={`text-xs ${benefit.highlight ? 'text-amber-200 font-semibold' : 'text-zinc-300'}`}>
                  {benefit.text}
                </span>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div className="text-center mb-5 py-4 bg-zinc-900/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-3xl font-bold text-amber-500 font-data">$49.97</span>
              <span className="text-zinc-500 text-sm">first month</span>
            </div>
            <p className="text-xs text-zinc-500">Then $99.97/month • Cancel anytime</p>
          </div>

          {/* CTA */}
          <Button
            onClick={handleUpgrade}
            className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-5"
            data-testid="upgrade-popup-btn"
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade to Pro Ecopreneur
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-zinc-900/50 border-t border-zinc-800">
          <p className="text-xs text-zinc-600 text-center">
            Secure payment via Square • Educational purposes only
          </p>
        </div>
      </div>
    </div>
  );
}
